import React, { useState, useMemo, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { cn, mapEdgesToNodes } from "../../lib/helpers";
import { ImageBuilder } from "../global/image-builder";
import { CardGrid } from "../card/card-grid";
import { SUGGESTION_INSIGHTS } from "./suggestion-insights";

const LOGO_IMAGE_SIZE = 36;

const suggestMachineQuery = graphql`
  query SuggestMachineQuery {
    docs: allSanityIntegration(
      sort: { fields: [company], order: ASC }
      filter: { status: { in: ["published", "hidden"] } }
    ) {
      edges {
        node {
          _type
          _id
          company
          sourceType
          mainImage {
            ...SanityImageSimple
          }
          mainImageSolid {
            ...SanityImageSimple
          }
          categories {
            _id
          }
        }
      }
    }
    playbooks: allSanityPlaybook(
      sort: { fields: [_createdAt], order: DESC }
      filter: { isPublished: { eq: true } }
    ) {
      edges {
        node {
          _type
          _id
          title
          slug {
            current
          }
          _rawExcerpt
          categories {
            _id
            slug {
              current
            }
          }
          integrations {
            _id
            company
            mainImage {
              ...SanityImageSimple
            }
          }
          useCases {
            _id
            title
            titlePublic
            slug {
              current
            }
            status
          }
        }
      }
    }
    stories: allSanityStory(
      sort: { fields: [orderRank], order: ASC }
      filter: {
        slug: { current: { ne: null } }
        status: { eq: "published" }
      }
    ) {
      edges {
        node {
          _type
          _createdAt
          _updatedAt
          publishedAt
          _id
          isFeatured
          categories {
            _id
            title
            titlePublic
            slug {
              current
            }
            status
          }
          title
          _rawExcerpt
          status
          slug {
            current
          }
          company {
            ...cardNodeCompany
          }
          useCases
          integrations {
            _id
            company
            mainImage {
              ...SanityImageSimple
            }
          }
        }
      }
    }
  }
`;

const TEAMS = [
  { id: "sales", label: "Sales", categorySlug: "sales" },
  { id: "marketing", label: "Marketing", categorySlug: "marketing" },
  { id: "revops", label: "RevOps", categorySlug: "revops" },
  { id: "community", label: "Community", categorySlug: "community-teams" },
];

const CATEGORIES = [
  {
    label: "Social & community",
    types: ["social", "code", "community"],
  },
  {
    label: "Business",
    types: ["gtm"],
  },
  {
    label: "Product",
    types: ["product"],
  },
  {
    label: "Custom",
    types: ["custom"],
  },
];

const STORIES_USECASES_STRINGS = [
  "AI agents",
  "Account-based sales",
  "GTM workbench",
  "Job-change tracking",
  "Lead and account scoring",
  "Marketing campaigns",
  "Product activity tracking",
  "Social listening",
  "Social prospecting",
  "Unified customer view",
  "Website activity tracking",
  "Workflow automation",
];
const PLAYBOOK_USECASES_VALUES = [
  "Accelerate Deals",
  "Break Into Account",
  "Competitive Takeout",
  "Convert High Intent Web Traffic",
  "Convert Social & Community Signals",
  "Drive Expansion",
  "Find New Accounts",
  "Find New Contacts",
  "Find Warm Intros",
  "Leverage Product Usage",
  "Multi-Thread",
  "Personalize Outreach",
  "Re-Engage Dormant Leads",
  "Target Key Accounts",
];

export const SuggestMachine = ({ section }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedSignals, setSelectedSignals] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [isRoomie, setIsRoomie] = useState(false);

  const { docs, playbooks, stories } = useStaticQuery(suggestMachineQuery) || {};
  const integrations = mapEdgesToNodes(docs);
  const allPlaybooks = mapEdgesToNodes(playbooks);
  const allStories = mapEdgesToNodes(stories);

  // Extract unique use cases from playbooks
  const useCases = useMemo(() => {
    const useCaseMap = new Map();
    allPlaybooks.forEach((playbook) => {
      playbook.useCases?.forEach((useCase) => {
        if (useCase._id && !useCaseMap.has(useCase._id)) {
          useCaseMap.set(useCase._id, useCase);
        }
      });
    });
    return Array.from(useCaseMap.values()).sort((a, b) => {
      const aTitle = a.titlePublic || a.title || '';
      const bTitle = b.titlePublic || b.title || '';
      return aTitle.localeCompare(bTitle);
    });
  }, [allPlaybooks]);

  // Extract unique use-case strings from stories (for debugging/logging)
  const storyUseCaseStrings = useMemo(() => {
    const useCaseSet = new Set();
    allStories.forEach((story) => {
      story.useCases?.forEach((useCaseString) => {
        if (useCaseString && typeof useCaseString === 'string') {
          useCaseSet.add(useCaseString);
        }
      });
    });
    return Array.from(useCaseSet).sort();
  }, [allStories]);

  // Log unique story use-case strings to console
  useEffect(() => {
    if (storyUseCaseStrings.length > 0) {
      console.log('Unique story use-case strings:', storyUseCaseStrings);
    }
  }, [storyUseCaseStrings]);

  // Filter playbooks based on selected team and categories only
  const filteredPlaybooks = useMemo(() => {
    // Show 3 most recent playbooks if no team, categories, or use-case are selected
    if (!selectedTeam && selectedCategories.size === 0 && !selectedUseCase) {
      return allPlaybooks.slice(0, 3);
    }

    return allPlaybooks.filter((playbook) => {
      let teamMatch = true;
      let categoryMatch = true;

      // Check team match
      if (selectedTeam) {
        teamMatch = playbook.categories?.some(
          (cat) => cat.slug?.current === TEAMS.find(t => t.id === selectedTeam)?.categorySlug
        );
      }

      // Check category match - playbook must have integrations in selected categories
      if (selectedCategories.size > 0) {
        categoryMatch = playbook.integrations?.some((integration) => {
          const integrationData = integrations.find(i => i._id === integration._id);
          if (!integrationData) return false;

          // Check if integration's sourceType is in any selected category
          return Array.from(selectedCategories).some(categoryLabel => {
            const category = CATEGORIES.find(c => c.label === categoryLabel);
            return category && category.types.includes(integrationData.sourceType);
          });
        });
      }

      return teamMatch && categoryMatch;
    });
  }, [selectedTeam, selectedCategories, allPlaybooks, integrations]);

  // Filter stories based on selected team and categories (NOT use-cases, since stories have string array)
  const filteredStories = useMemo(() => {
    // Show 3 most recent stories if no team or categories are selected
    if (!selectedTeam && selectedCategories.size === 0) {
      return allStories.slice(0, 3);
    }

    return allStories.filter((story) => {
      let teamMatch = true;
      let categoryMatch = true;

      // Check team match
      if (selectedTeam) {
        teamMatch = story.categories?.some(
          (cat) => cat.slug?.current === TEAMS.find(t => t.id === selectedTeam)?.categorySlug
        );
      }

      // Check category match - story must have integrations in selected categories
      if (selectedCategories.size > 0) {
        categoryMatch = story.integrations?.some((integration) => {
          const integrationData = integrations.find(i => i._id === integration._id);
          if (!integrationData) return false;

          // Check if integration's sourceType is in any selected category
          return Array.from(selectedCategories).some(categoryLabel => {
            const category = CATEGORIES.find(c => c.label === categoryLabel);
            return category && category.types.includes(integrationData.sourceType);
          });
        });
      }

      return teamMatch && categoryMatch;
    });
  }, [selectedTeam, selectedCategories, allStories, integrations]);

  // Combine filtered playbooks and stories
  const filteredContent = useMemo(() => {
    return [...filteredPlaybooks, ...filteredStories];
  }, [filteredPlaybooks, filteredStories]);

  // Determine which content items should be highlighted (use-case OR individual signals)
  const highlightedContentIds = useMemo(() => {
    const highlighted = new Set();

    filteredContent.forEach((item) => {
      let shouldHighlight = false;

      // Check if item matches selected use case (only for playbooks, not stories)
      // Stories have useCases as string array, playbooks have object array with _id
      if (selectedUseCase && item._type === 'playbook' && item.useCases?.some(uc => uc._id === selectedUseCase)) {
        shouldHighlight = true;
      }

      // Check if item matches any selected individual signal (works for both playbooks and stories)
      if (selectedSignals.size > 0 && item.integrations?.some(
        (integration) => selectedSignals.has(integration._id)
      )) {
        shouldHighlight = true;
      }

      if (shouldHighlight) {
        highlighted.add(item._id);
      }
    });

    return highlighted;
  }, [filteredContent, selectedUseCase, selectedSignals]);

  // Find matching insight when team and exactly one category are selected
  const currentInsight = useMemo(() => {
    if (!selectedTeam || selectedCategories.size !== 1) {
      return null;
    }

    const selectedCategoryLabel = Array.from(selectedCategories)[0];

    // Map category label to insight category type
    const categoryMapping = {
      "Social & community": "social",
      "Business": "gtm",
      "Product": "product",
      "Custom": "custom"
    };

    const insightCategory = categoryMapping[selectedCategoryLabel];
    if (!insightCategory) return null;

    return SUGGESTION_INSIGHTS.find(
      insight => insight.team === selectedTeam && insight.integrationCategory === insightCategory
    );
  }, [selectedTeam, selectedCategories]);

  // Check if user has selected enough data points (3 total: team, category, and signal/use-case)
  const readyToBuild = useMemo(() => {
    return selectedTeam &&
      selectedCategories.size > 0 &&
      (selectedSignals.size > 0 || selectedUseCase);
  }, [selectedTeam, selectedCategories, selectedSignals, selectedUseCase]);

  const handleTeamClick = (teamId) => {
    setSelectedTeam((prev) => (prev === teamId ? null : teamId));
  };

  const handleSignalToggle = (integrationId, event) => {
    // Stop event from bubbling to parent div
    event.stopPropagation();

    // Find the integration and its category before updating state
    const integration = integrations.find(i => i._id === integrationId);
    const isCurrentlySelected = selectedSignals.has(integrationId);

    // If selecting (not deselecting), add the category
    if (!isCurrentlySelected && integration) {
      const matchingCategory = CATEGORIES.find(cat =>
        cat.types.includes(integration.sourceType)
      );
      if (matchingCategory) {
        setSelectedCategories((prevCats) => {
          const newCatSet = new Set(prevCats);
          newCatSet.add(matchingCategory.label);
          return newCatSet;
        });
      }
    }

    // Toggle the signal
    setSelectedSignals((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(integrationId)) {
        newSet.delete(integrationId);
      } else {
        newSet.add(integrationId);
      }
      return newSet;
    });
  };

  const handleClearAll = () => {
    setSelectedSignals(new Set());
    setSelectedCategories(new Set());
  };

  const handleCategoryToggle = (categoryLabel) => {
    setSelectedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryLabel)) {
        newSet.delete(categoryLabel);
      } else {
        newSet.add(categoryLabel);
      }
      return newSet;
    });
  };

  const handleUseCaseChange = (e) => {
    setSelectedUseCase(e.target.value || null);
  };

  const handleGenerateReport = () => {
    if (!readyToBuild) return;

    // Get team name
    const teamName = TEAMS.find(t => t.id === selectedTeam)?.label || "None";

    // Get category names
    const categoryNames = Array.from(selectedCategories).join(", ");

    // Get signal names
    const signalNames = Array.from(selectedSignals)
      .map(signalId => {
        const integration = integrations.find(i => i._id === signalId);
        return integration?.company;
      })
      .filter(Boolean)
      .join(", ");

    // Get use case name
    const useCaseName = selectedUseCase
      ? (useCases.find(uc => uc._id === selectedUseCase)?.titlePublic ||
        useCases.find(uc => uc._id === selectedUseCase)?.title ||
        "Unknown")
      : "None";

    const reportInfo = `Just a demo!\n\nReport would include:\n\nTeam: ${teamName}\nCategories: ${categoryNames}\nSignals: ${signalNames || 'None'}\nUse Case: ${useCaseName}\nContent: ${filteredContent.length} matching items (playbooks + stories)`;

    alert(reportInfo);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("roomie")) {
      setIsRoomie(true);
      console.log("Roomie mode activated");
    }
  }, []);

  return (
    <>
      <div className="space-y-6 bg-dark-5 p-4 rounded-xl">
        <div className="flex gap-8">
          <div>
            {/* Team Selection */}
            <div className="flex items-center mb-2 gap-4">
              <h3 className="text-xl font-semibold">
                Your Team:
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {TEAMS.map((team) => (
                <button
                  key={team.id}
                  onClick={() => handleTeamClick(team.id)}
                  className={`px-6 py-2 rounded-lg font-medium transition-all focus:outline-none ${selectedTeam === team.id
                    ? "bg-dark-80 text-white shadow-lg"
                    : "bg-dark-10 text-dark-70 hover:bg-green-emerald"
                    }`}
                >
                  {team.label}
                </button>
              ))}
            </div>
          </div>

          {/* Use-Case Selection */}
          <div>
            <div className="flex items-center mb-2 gap-4">
              <h3 className="text-xl font-semibold">
                Use-Case:
              </h3>
            </div>
            <select
              value={selectedUseCase || ''}
              onChange={handleUseCaseChange}
              className="w-full px-4 py-2 rounded-lg border-2 border-dark-20 bg-dark-5 focus:outline-none focus:border-black transition-all"
            >
              <option value="">Select a use-case...</option>
              {useCases.map((useCase) => (
                <option key={useCase._id} value={useCase._id}>
                  {useCase.titlePublic || useCase.title}
                </option>
              ))}
            </select>
          </div>

        </div>

        {/* Integration Signals Grid by Category */}
        <div className="mb-2">
          <div className="flex items-center mb-3 gap-4">
            <h3 className="text-xl font-semibold">
              Signals you care about:
            </h3>
            <button
              onClick={handleClearAll}
              className="text-sm text-dark-70 hover:underline focus:outline-none"
            >
              Clear all
            </button>
          </div>

          <div className="flex gap-2">
            {CATEGORIES.map((category) => {
              const categoryIntegrations = integrations
                .filter(
                  (integration) =>
                    integration.sourceType &&
                    category.types.includes(integration.sourceType) &&
                    (integration.mainImageSolid || integration.mainImage)
                )
                .sort((a, b) => {
                  const aHasPlaybooks = allPlaybooks.some(playbook =>
                    playbook.integrations?.some(int => int._id === a._id)
                  );
                  const bHasPlaybooks = allPlaybooks.some(playbook =>
                    playbook.integrations?.some(int => int._id === b._id)
                  );
                  // Sort with hasPlaybooks true first (return -1 if a should come before b)
                  if (aHasPlaybooks && !bHasPlaybooks) return -1;
                  if (!aHasPlaybooks && bHasPlaybooks) return 1;
                  return 0;
                });

              if (categoryIntegrations.length === 0) return null;

              const isCategorySelected = selectedCategories.has(category.label);

              return (
                <div
                  key={category.label}
                  onClick={() => handleCategoryToggle(category.label)}
                  className={`border-2 bg-dark-2 rounded-xl p-1.5 transition-all cursor-pointer ${isCategorySelected
                    ? "border-black shadow-md bg-dark-5"
                    : "border-dark-10 hover:border-black"
                    }`}
                >
                  <h4 className={`ml-1 mb-1 font-medium ${isCategorySelected ? "text-black" : "text-dark-60"
                    }`}>
                    {category.label}
                  </h4>
                  <div className="flex flex-wrap gap-0.5" style={{ minWidth: "8rem" }}>
                    {categoryIntegrations.map((integration) => {
                      const isSelected = selectedSignals.has(integration._id);
                      const image = integration.mainImageSolid || integration.mainImage;
                      const hasPlaybooks = allPlaybooks.some(playbook =>
                        playbook.integrations?.some(int => int._id === integration._id)
                      );
                      return (
                        <button
                          key={integration._id}
                          onClick={(e) => handleSignalToggle(integration._id, e)}
                          className={`relative p-0.5 transition-size border-2 rounded-xl focus:outline-none hover:scale-105 ${!hasPlaybooks
                            ? `${isSelected
                              ? "border-black shadow-sm"
                              : "border-dark-10 hover:border-dark-30"
                            }`
                            : `${isSelected
                              ? "border-black shadow-md"
                              : "border-dark-30 hover:border-black"
                            }`
                            }`}
                          title={hasPlaybooks ? integration.company : `${integration.company} (no playbooks available)`}
                        >
                          <ImageBuilder
                            className="rounded-lg"
                            image={image}
                            height={LOGO_IMAGE_SIZE}
                            width={LOGO_IMAGE_SIZE}
                            quality={100}
                            alt={`${integration.company} logo`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {/** Selection Status */}
          <div className="mt-4 flex items-center gap-4">
            <p className="text-dark-40">
              {selectedCategories.size} categories selected, {selectedSignals.size} signals selected, {selectedUseCase ? '1' : '0'} use-case selected, {filteredContent.length} {filteredContent.length === 1 ? 'item' : 'items'}
            </p>
            {isRoomie && (
              <button
                onClick={handleGenerateReport}
                disabled={!readyToBuild}
                className={cn("rounded-md px-4 py-1",
                  readyToBuild ? "bg-green-emerald text-black hover:bg-green-emerald focus:outline-none focus:ring-2 focus:ring-green-emerald focus:ring-offset-2"
                    : "bg-dark-10 text-dark-50 cursor-not-allowed"
                )}>
                Generate Insights Report
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Recommended Playbooks */}
      <div className="mt-16" style={{ minHeight: "400px" }}>
        {currentInsight && (
          <div className="mb-4 p-4 bg-dark-5 rounded-xl">
            <h4 className="text-lg font-medium mb-1">
              Insights for {currentInsight.team} and {currentInsight.integrationCategory} signals
            </h4>
            <p className="leading-snug">
              {currentInsight.insights}
            </p>
          </div>
        )}
        {filteredContent.length > 0 && (
          <>
            <CardGrid
              maxColumns={3}
              nodes={filteredContent
                .map(item => ({
                  ...item,
                  highlighted: highlightedContentIds.has(item._id)
                }))
                .sort((a, b) => {
                  // Sort highlighted items to the top
                  if (a.highlighted && !b.highlighted) return -1;
                  if (!a.highlighted && b.highlighted) return 1;
                  return 0;
                })}
              hideEvents={true}
              hideLabels={true}
              hideDate={true}
              showSummary={true}
              panels={true}
              makeThumbnail={true}
            />
          </>
        )}
      </div>
    </>
  );
};
